import { useEffect, useMemo } from "react";

import { JiraLogo } from "@atlaskit/logo";

import { SignupBackgroundLayout } from "@/components/signup/layouts";
import { ProductKey } from "@/components/signup/utils";
import {
    useConfluenceD0to6,
    useJiraD0to6,
    useUserIsManagedByGuardOrgAndHasSites
} from "@/components/user-context";
import { BgColors } from "@/libraries/design/components/link/src/link";
import { experiments, useExperiment } from "@/utils/experiment";
import { FeatureExposed } from "@/utils/feature-exposed";
import { useLocation } from "@/utils/location-provider";

import { ufoEventTryCloudSignupPageLoad } from "../../../../utils";
import { SignupModuleWrapper } from "../../signup-module-wrapper";
import { useSignupFormContext } from "../signup-form/hooks";

import { GuardReturnToProduct } from "./components/guard-return-to-product";
import { DefaultSiteName, ContactExpand } from "./components/variants";
import { Adg4SiteNameForm } from "./components/variants/atlas-66195-cobranding/site-name-form";
import { CobrandedContactExpand } from "./components/variants/contact-expand/cobranded-contact-expand";
import {
    CobrandedProductLogo,
    ConfluenceNewUserWorkspaceNameForm
} from "./components/variants/new-user-confluence-workspace-name";
import { SiteNameContextProvider } from "./site-name-context";

export const SiteNameForm = ({
    isNewUser,
    hasRebrandedSignupFormStyles,
    bgColor
}: {
    isNewUser?: boolean;
    hasRebrandedSignupFormStyles?: boolean;
    bgColor?: BgColors;
}) => {
    const { productKey } = useSignupFormContext();
    const { pathname, search } = useLocation();
    const queryParams = new URLSearchParams(search);

    const hasConfluenceD0to6 = useConfluenceD0to6();
    const hasJiraD0to6 = useJiraD0to6();

    const userIsManagedByGuardOrgAndHasSites =
        useUserIsManagedByGuardOrgAndHasSites();

    // guard only shows up on the signup page
    const guardReturnToProductEnabled = pathname.endsWith("/try/cloud/signup");

    const shouldShowGuardReturnToProduct =
        guardReturnToProductEnabled && userIsManagedByGuardOrgAndHasSites;

    const loggedInConfluenceFreeSignupCobranding = useExperiment(
        experiments.loggedInConfluenceFreeSignupCobranding
    );

    const variant = useMemo(() => {
        if (shouldShowGuardReturnToProduct) {
            return <GuardReturnToProduct />;
        }

        if (hasRebrandedSignupFormStyles) {
            return (
                <SignupModuleWrapper
                    hideFooter={true}
                    fullPage={false}
                    fullHeight={false}
                >
                    <Adg4SiteNameForm
                        showSubheader={false}
                        isOnDarkBackground={
                            bgColor === "bgWhite" ? false : true
                        }
                    />
                </SignupModuleWrapper>
            );
        }

        if (productKey === ProductKey.JIRA_SOFTWARE) {
            return (
                <SignupBackgroundLayout
                    ProductLogo={<JiraLogo size="xlarge" appearance="brand" />}
                >
                    {hasJiraD0to6 ? (
                        <FeatureExposed
                            experimentKey="jfind-contact-expand-ii"
                            group="Test"
                        >
                            <CobrandedContactExpand
                                productKeyForExpand={ProductKey.JIRA_SOFTWARE}
                            />
                        </FeatureExposed>
                    ) : (
                        <Adg4SiteNameForm />
                    )}
                </SignupBackgroundLayout>
            );
        }
        if (
            productKey === ProductKey.CONFLUENCE &&
            !isNewUser &&
            loggedInConfluenceFreeSignupCobranding.type === "enrolled"
        ) {
            loggedInConfluenceFreeSignupCobranding.fireExposure();

            if (loggedInConfluenceFreeSignupCobranding.value) {
                return (
                    <SignupBackgroundLayout
                        ProductLogo={<CobrandedProductLogo />}
                        page="EXISTING_USER_WORKSPACE_FROM"
                    >
                        {hasConfluenceD0to6 ? (
                            <CobrandedContactExpand
                                productKeyForExpand={ProductKey.CONFLUENCE}
                            />
                        ) : (
                            <Adg4SiteNameForm />
                        )}
                    </SignupBackgroundLayout>
                );
            }
        }

        if (productKey === ProductKey.CONFLUENCE) {
            // Do not show contact expand if user clicked on a "create new" link
            const createNewWorkspace = queryParams.get("createNew") === "true";

            if (isNewUser) {
                return (
                    <SignupBackgroundLayout
                        ProductLogo={<CobrandedProductLogo />}
                        page="NEW_USER_WORKSPACE_FORM"
                    >
                        <ConfluenceNewUserWorkspaceNameForm />
                    </SignupBackgroundLayout>
                );
            } else if (!createNewWorkspace && hasConfluenceD0to6) {
                return (
                    <FeatureExposed
                        experimentKey="cfind-2658_redirect_contact_expand_to_product"
                        group="Test"
                    >
                        <SignupModuleWrapper>
                            <ContactExpand
                                productKeyForExpand={ProductKey.CONFLUENCE}
                            />
                        </SignupModuleWrapper>
                    </FeatureExposed>
                );
            }
        }

        return (
            <SignupModuleWrapper>
                <DefaultSiteName />
            </SignupModuleWrapper>
        );
    }, [
        shouldShowGuardReturnToProduct,
        hasJiraD0to6,
        hasRebrandedSignupFormStyles,
        productKey,
        isNewUser,
        hasConfluenceD0to6,
        bgColor,
        loggedInConfluenceFreeSignupCobranding
    ]);

    useEffect(() => {
        const customData = {
            webPlatform: "bxp-ui",
            reactComponent: "SiteNameForm",
            isLoggedIn: true
        };
        ufoEventTryCloudSignupPageLoad.success({
            customData
        });
    }, []);

    return (
        <SiteNameContextProvider productKey={productKey} isNewUser={isNewUser}>
            {variant}
        </SiteNameContextProvider>
    );
};
